import FeatureGates from '@atlaskit/feature-gate-js-client';
import { History } from 'history';
import React, { Suspense, useCallback } from 'react';
import { PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';
import { MatchedRoute } from 'react-resource-router';

import { useAnalytics } from '@townsquare/analytics';
import { AppState, useAppStateStore } from '@townsquare/app-state-store';
import { getConfig } from '@townsquare/config';
import { redirect } from '@townsquare/facade/window';
import { featureFlagClient, FeatureFlagClientInterface, initFeatureFlagClient } from '@townsquare/feature-flags';
import { filterNull, filterUndefined } from '@townsquare/filter-type';
import { useAsyncEffect } from '@townsquare/hooks';
import { initBM3 } from '@townsquare/monitoring/bm3';
import { initUFO } from '@townsquare/monitoring/ufo';
import { useOrgContextActions } from '@townsquare/org-context';
import { createOrgScopedPath } from '@townsquare/router/create-route';
import { matchRoute } from '@townsquare/router/primitives';
import type { GlobalHomePageRoute, SiteHomePageRoute } from '@townsquare/router/route-types';
import { featureGatesOptions } from '@townsquare/stat-sig';
import { createSearchParamsFromCurrentUrl, createSearchParamsFromScratch } from '@townsquare/url-utils/search-params';
import { useCurrentUser } from '@townsquare/user-store';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';
import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen } from './ErrorBoundary/scenarios';
import { BootstrapQuery } from './__generated__/BootstrapQuery.graphql';
import { preloadedQueryRef } from './preload';
import { preloadRouteComponent } from './preloadRouteComponent';

type BootstrapResult = {
  orgId?: string;
  cloudIds: string[];
  workspaceIds: string[];
  workspaceUuids: string[];
  workspace?: WorkspaceDetails;
  user: User;
  featureFlags: FeatureFlagClientInterface;
};

type BootstrapProps = {
  children: (data: BootstrapResult) => JSX.Element;
  matchedRoute: MatchedRoute | null;
  history: History<unknown>;
  queryRef: PreloadedQuery<BootstrapQuery>;
};

function isGlobalRoute(route: MatchedRoute['route']): route is GlobalHomePageRoute {
  return (route as GlobalHomePageRoute | SiteHomePageRoute)?.routeType === 'global';
}

export function BootstrapResolution(props: BootstrapProps): JSX.Element {
  const data = usePreloadedQuery(
    graphql`
      query BootstrapQuery($context: OrganizationContextInput) {
        userOrganizationByContext(organizationContext: $context) {
          orgId
          cloudIds
          workspaces {
            id
            uuid
            cloudId
            type
            ...storeHomeWorkspace
          }
        }
      }
    `,
    props.queryRef,
  );

  const [{ appState }, { setAppState }] = useAppStateStore();

  const orgId = data.userOrganizationByContext?.orgId;
  const cloudIds = data.userOrganizationByContext?.cloudIds?.filter(filterNull) ?? [];
  const workspaces = data.userOrganizationByContext?.workspaces?.filter(filterNull) ?? [];
  const workspaceIds = workspaces?.map(workspace => workspace?.id).filter(filterUndefined) ?? [];
  const workspaceUuids = workspaces?.map(workspace => workspace?.uuid).filter(filterUndefined) ?? [];

  const orgContextActions = useOrgContextActions();
  if (orgId) {
    orgContextActions.setOrgContext({
      orgId,
      cloudIds,
      workspaceIds,
      workspaceUuids,
    });

    // This should only trigger for global routes
    const matchedRoute = props.matchedRoute;
    if (
      matchedRoute?.match.params.orgIdentifier !== orgId &&
      matchedRoute?.route &&
      isGlobalRoute(matchedRoute.route) &&
      matchedRoute.route.name !== 'not-found'
    ) {
      const { path, pathPattern } = matchedRoute.route;
      const { query } = matchedRoute.match;
      props.history.replace(createOrgScopedPath(`${pathPattern}${path}`, orgId, query, matchedRoute.match.params));
    }
  }

  /**
   * We are temporarily using the first workspace as the default workspace
   * To aid our migration to a multi-workspace world.
   * This will enable us to stand up key pieces for Team, once we have
   * better support for multi-workspace, this will be removed.
   */
  const contextCloudId = props.queryRef.variables.context?.cloudId;

  const workspace =
    workspaces.find(workspace => workspace.cloudId === contextCloudId) ??
    workspaces.find(workspace => workspace.type === 'GLOBAL_EXPERIENCE') ??
    workspaces.at(0);
  const workspaceActions = useWorkspaceActions();
  let workspaceDetails: WorkspaceDetails | undefined;
  if (workspace) {
    workspaceDetails = workspaceActions.initWorkspaceFromHome(workspace, workspaceIds.length);
  }

  const currentUser = useCurrentUser();
  const analytics = useAnalytics();

  const completeInit = useCallback(() => {
    // Initialize monitoring
    initBM3(analytics.getAnalyticsWebClient(), featureFlagClient, 'townsquare');
    initUFO(analytics.getAnalyticsWebClient(), 'townsquare');

    // Set the analytics client ready
    analytics.setCurrentUserAndTenantInfo(currentUser.accountId, workspaceDetails?.UUID, workspaceDetails?.cloudId);

    setAppState(AppState.FULLY_INITIALISED);
  }, [analytics, currentUser.accountId, workspaceDetails?.UUID, workspaceDetails?.cloudId, setAppState]);

  useAsyncEffect(async () => {
    // If we find a product workspace redirect to the classic app
    // This is needed until migration of PRODUCT -> GLOBAL_EXPERIENCE is complete
    if (workspaceDetails && workspaceDetails.type === 'PRODUCT') {
      const qs = createSearchParamsFromScratch();
      if (workspaceDetails?.cloudId) {
        qs.set('cloudId', workspaceDetails.cloudId);
      }

      const query = qs.get('cloudId') ? '?' + qs : '';
      redirect(getConfig().classicUrl + query);
      return;
    }

    if (workspaceDetails && orgId) {
      await FeatureGates.initialize(featureGatesOptions, {
        atlassianAccountId: currentUser.accountId,
        tenantId: workspaceDetails.cloudId,
        atlassianOrgId: orgId,
      });
    }

    // If we have a matched route and it has a preload function, call it
    // This will warm up the route for faster navigation
    if (props.matchedRoute?.route) {
      preloadRouteComponent(props.matchedRoute.route);
    }

    const analyticsClient = analytics.getAnalyticsWebClient();
    initFeatureFlagClient(analyticsClient, currentUser.accountId, workspaceDetails?.UUID, workspaceDetails?.cloudId)
      .ready()
      .finally(completeInit);
  }, [currentUser.accountId, workspaceDetails, analytics, completeInit, props.matchedRoute?.route]);

  if (appState === AppState.LOADING) {
    return <FullScreenSpaLoader />;
  }

  return props.children({
    orgId: data.userOrganizationByContext?.orgId ?? undefined,
    cloudIds,
    workspaceIds,
    workspaceUuids,
    workspace: workspaceDetails,
    user: currentUser,
    featureFlags: featureFlagClient,
  });
}

export function Bootstrap(props: Omit<BootstrapProps, 'queryRef' | 'matchedRoute'>) {
  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);

  if (matchedRoute?.route.name === '404-page') {
    return <NotFoundScreen />;
  }

  return (
    <BootstrapErrorBoundary>
      <Suspense fallback={<FullScreenSpaLoader />}>
        <BootstrapResolution {...props} queryRef={preloadedQueryRef} matchedRoute={matchedRoute} />
      </Suspense>
    </BootstrapErrorBoundary>
  );
}
